import React from "react";
import { Box, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { shortLabel } from "../utils/policyParts";
import { STAGE_ORDER, doneCount, insurerOf, nextStage, stageParts, stageStatusMeta, stageTitle } from "../utils/relianceStages";

const NAVY = "#1E3A5F";
const FONT = "Inter, Poppins, sans-serif";

// The same palette as the History dialog's step cards (RelianceStageList)
const LOOK = {
  completed: { color: "#067647", bg: "#ECFDF3", border: "#ABEFC6" },
  failed: { color: "#B42318", bg: "#FEF3F2", border: "#FECDCA" },
  next: { color: "#175CD3", bg: "#EFF8FF", border: "#B2DDFF" },
  pending: { color: "#667085", bg: "#F9FAFB", border: "#EAECF0" },
};

/** Does this row have Reliance stages to show on hover? (the server sends relianceStageView; empty = not Reliance) */
export const hasStageView = (row) => stageParts(row && row.relianceStageView).length > 0;

/**
 * The small card shown when the pointer is on a row's History icon: each Reliance part's three steps with their status
 * — read-only (the History dialog is where a step is set back to Pending).
 */
const RelianceStagesHoverCard = ({ relianceStageView, paymentMethod, hideFooter = false, width = 248 }) => {
  const parts = stageParts(relianceStageView);
  if (!parts.length) return null;
  return (
    <Box data-testid="stages-hover-card" sx={{ width, fontFamily: FONT }}>
      {parts.map((part) => {
        const done = doneCount(part.stages);
        const next = nextStage(part.stages);
        const all = done === part.stages.length;
        return (
          <Box key={part.key} sx={{ "& + &": { mt: 1.25, pt: 1.25, borderTop: "1px solid #F2F4F7" } }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
              <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: NAVY, fontFamily: FONT, lineHeight: "18px" }}>
                {insurerOf(part)} {part.label || shortLabel(part.key)}
              </Typography>
              <Box
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  lineHeight: "18px",
                  px: 1,
                  borderRadius: "999px",
                  whiteSpace: "nowrap",
                  color: all ? LOOK.completed.color : "#475467",
                  backgroundColor: all ? LOOK.completed.bg : "#F2F4F7",
                }}
              >
                {done} of {part.stages.length} done
              </Box>
            </Box>
            {part.stages.map((row) => {
              const meta = stageStatusMeta(row.status);
              const key = meta.status === "pending" && row.stage === next ? "next" : meta.status;
              const look = LOOK[key];
              const filled = meta.status !== "pending";
              return (
                <Box key={row.stage} data-testid={`hover-stage-${part.key}-${row.stage}`} sx={{ display: "flex", alignItems: "center", gap: "8px", minHeight: 26 }}>
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      flexShrink: 0,
                      boxSizing: "border-box",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "10.5px",
                      fontWeight: 700,
                      color: filled ? "#fff" : look.color,
                      backgroundColor: filled ? look.color : "#fff",
                      border: `1.5px solid ${look.color}`,
                      "& svg": { fontSize: 12 },
                    }}
                  >
                    {meta.status === "completed" ? <CheckRoundedIcon /> : meta.status === "failed" ? <CloseRoundedIcon /> : STAGE_ORDER.indexOf(row.stage) + 1}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: NAVY, fontFamily: FONT, lineHeight: "16px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {stageTitle(row, { paymentMethod, company: part.company }).replace(/^\d+ /, "")}
                    </Typography>
                    {row.stage === "proposal" && row.proposalNumber ? (
                      <Typography sx={{ fontSize: "10.5px", color: "#667085", fontFamily: FONT, lineHeight: "14px" }}>{row.proposalNumber}</Typography>
                    ) : null}
                  </Box>
                  <Box
                    sx={{
                      flexShrink: 0,
                      fontSize: "10.5px",
                      fontWeight: 700,
                      lineHeight: "16px",
                      px: "7px",
                      borderRadius: "999px",
                      whiteSpace: "nowrap",
                      color: look.color,
                      backgroundColor: look.bg,
                      border: `1px solid ${look.border}`,
                    }}
                  >
                    {key === "next" ? "Up next" : meta.label}
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      })}
      {hideFooter ? null : (
        <Typography sx={{ mt: 1, pt: 1, borderTop: "1px solid #F2F4F7", fontSize: "11px", color: "#98A2B3", fontFamily: FONT }}>
          Click for the full history
        </Typography>
      )}
    </Box>
  );
};

export default RelianceStagesHoverCard;
